import React, { useMemo, useState, useCallback } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import { CircularProgress, Tooltip, IconButton, Link, Box } from '@mui/material'
import { Edit, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

// Third-party Imports
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel
} from '@tanstack/react-table'

// Component Imports
import TablePaginationComponent from '../molecules/TablePaginationComponent'

// Utils Imports
import { getSuitabilityLevelInfo } from '../../../utils/helpers'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

const columnHelper = createColumnHelper()

const getFirstWebsite = (web) => {
  if (!web) return null

  const websites = web.split(/[,;]/).map(w => w.trim()).filter(w => w)
  if (websites.length === 0) return null
  const firstWebsite = websites[0]

  return firstWebsite.indexOf('://') === -1 ? `http://${firstWebsite}` : firstWebsite
}

const CampaignReviewTable = (props) => {
  const {
    data,
    onEdit,
    fetching,
    pageSize = 10,
    disableInteraction = false,
    campaignStatus,
    fuzzyFilter,
    globalFilter,
    setGlobalFilter,
    currentPage = 0,
    onPageChange
  } = props
  const { t } = useTranslation()

  const [expandedRows, setExpandedRows] = useState({})

  const toggleRowExpanded = useCallback((rowId) => {
    setExpandedRows(prev => {
      if (prev[rowId]) {
        return {}
      }
      return { [rowId]: true }
    })
  }, [])

  const getSelectedContacts = useCallback((firm) => {
    if (!firm.contacts) return []

    return firm.contacts.filter(contact => contact.selected === true)
  }, [])

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          id: 'expander',
          header: '',
          size: 40,
          cell: ({ row }) => {
            const selectedContacts = getSelectedContacts(row.original)
            if (selectedContacts.length === 0) return null

            const isExpanded = expandedRows[row.original.identifier]

            return (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleRowExpanded(row.original.identifier)
                }}
                sx={{ padding: '4px' }}
              >
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            )
          }
        },
        columnHelper.accessor('name', {
          header: t('mailTemplate.wizard.firmList.firmName'),
          meta: { flexGrow: 1 },
          cell: ({ row }) => {
            const firmName = row.original.name
            const website = getFirstWebsite(row.original.web)
            const selectedContacts = getSelectedContacts(row.original)

            const nameContent = (
              <span style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                maxWidth: '300px'
              }}>
                {firmName}
              </span>
            )

            return (
              <div className="flex items-center" style={{ width: '100%', minWidth: '120px' }}>
                <div className="flex flex-col" style={{ width: '100%' }}>
                  <Typography color="text.primary" className="font-medium">
                    <Tooltip
                      title={firmName}
                      arrow
                      placement="top"
                      disableHoverListener={firmName.length <= 40}
                    >
                      {website
                        ? (
                          <Link
                            href={website}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            underline="hover"
                            sx={{
                              color: 'inherit !important',
                              '&:hover': { color: 'inherit !important' },
                              '&:visited': { color: 'inherit !important' },
                              '&:active': { color: 'inherit !important' }
                            }}
                          >
                            {nameContent}
                          </Link>
                          )
                        : (
                            nameContent
                          )}
                    </Tooltip>
                  </Typography>
                  {selectedContacts.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedContacts.length} {t('mailTemplate.employeeSelection.contacts')}
                    </Typography>
                  )}
                </div>
              </div>
            )
          }
        }),
        columnHelper.accessor('selected', {
          header: t('mailTemplate.campaignReview.selected'),
          cell: ({ row }) => (
            <div className="flex items-center">
              <div className="flex flex-col">
                <Typography variant="body2" style={{ fontSize: '18px', lineHeight: 1, fontWeight: 'bold' }}>
                  {row.original.selected === true
                    ? '◉'
                    : row.original.selected === false
                      ? '○'
                      : '—'
                  }
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('suitabilityLevel', {
          header: t('mailTemplate.wizard.firmList.relevant'),
          sortingFn: (rowA, rowB) => {
            const levelOrder = { HIGHLY_SUITABLE: 1, MODERATELY_SUITABLE: 2, MINIMALLY_SUITABLE: 3, NOT_SUITABLE: 4, CANNOT_ACCESS: 5 }
            const levelA = levelOrder[rowA.original.suitabilityLevel] || 999
            const levelB = levelOrder[rowB.original.suitabilityLevel] || 999
            return levelA - levelB
          },
          cell: ({ row }) => {
            const suitabilityInfo = getSuitabilityLevelInfo(row.original.suitabilityLevel)

            return (
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1" style={{ alignItems: 'center' }}>
                  {suitabilityInfo
                    ? (
                      <Tooltip
                        title={t(suitabilityInfo.labelKey)}
                        arrow
                        placement="top"
                      >
                        <div
                          style={{
                            backgroundColor: suitabilityInfo.color,
                            color: 'white',
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          {suitabilityInfo.level}
                        </div>
                      </Tooltip>
                      )
                    : (
                      <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                      )}
                </div>
              </div>
            )
          }
        }),
        columnHelper.accessor('explanation', {
          header: t('mailTemplate.wizard.firmList.explanation'),
          meta: { flexGrow: 2 },
          cell: ({ row }) => {
            const explanation = row.original.explanation

            const decodedExplanation = explanation
              ? explanation.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, '\'').replace(/&nbsp;/g, ' ')
              : explanation
            return (
              <div className="flex items-center gap-4">
                <div className="flex flex-col" style={{ width: '100%', minWidth: '200px' }}>
                  {decodedExplanation
                    ? (
                      <Typography color="text.primary" className="font-medium">
                        <Tooltip
                          title={
                            <div style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 10,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {decodedExplanation}
                            </div>
                          }
                          arrow
                          placement="top"
                          disableHoverListener={decodedExplanation.length <= 80}
                        >
                        <span style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                          maxWidth: '470px'
                        }}>
                          {decodedExplanation}
                        </span>
                        </Tooltip>
                      </Typography>
                      )
                    : (
                      <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                      )}
                </div>
              </div>
            )
          }
        }),
        columnHelper.accessor('emailReadyContactCount', {
          header: t('mailTemplate.campaignReview.emailStatus'),
          cell: ({ row }) => {
            const isReadyToMail = campaignStatus === 'READY_TO_MAIL' || campaignStatus === 'MAILING'

            if (isReadyToMail) {
              return (
                <Typography
                  variant="body2"
                  color="success.main"
                  fontWeight="medium"
                >
                  {t('mailTemplate.campaignReview.allReady')}
                </Typography>
              )
            }

            const selectedContacts = getSelectedContacts(row.original)
            const totalSelected = selectedContacts.length
            const emailContentReadyCount = selectedContacts.filter(
              contact => contact.emailContent && contact.emailContent.trim() !== ''
            ).length

            const isComplete = totalSelected > 0 && emailContentReadyCount === totalSelected
            const pendingCount = totalSelected - emailContentReadyCount

            return (
              <Typography
                variant="body2"
                color={isComplete ? 'success.main' : 'warning.main'}
                fontWeight="medium"
              >
                {isComplete
                  ? t('mailTemplate.campaignReview.allReady')
                  : t('mailTemplate.campaignReview.pendingEmails', { count: pendingCount })}
              </Typography>
            )
          }
        })
      ]

      return baseColumns
    },
    [t, expandedRows, toggleRowExpanded, getSelectedContacts, campaignStatus]
  )

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter
    },
    state: {
      globalFilter,
      pagination: {
        pageIndex: currentPage,
        pageSize
      }
    },
    globalFilterFn: fuzzyFilter,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex: currentPage, pageSize })
        if (onPageChange && newState.pageIndex !== currentPage) {
          onPageChange(newState.pageIndex)
        }
      }
    },
    manualPagination: false,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getExpandedRowModel: getExpandedRowModel(),
    meta: {
      campaignStatus
    }
  })

  const renderContactSubRow = (contact, firm) => {
    const hasNoSubject = !contact.emailSubject
    const hasNoContent = !contact.emailContent
    const isEdited = contact.emailContentEdited === true || contact.emailContentEdited === 'true'
    const isReadyStatus = campaignStatus === 'READY_TO_MAIL' || campaignStatus === 'MAILING'
    const showGeneratedMessage = isReadyStatus

    const shouldHighlightEdited = isReadyStatus && isEdited
    const shouldHighlightEmpty = isReadyStatus && !isEdited && (hasNoSubject || hasNoContent)

    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
    const isUnknownContact = !fullName

    const contactName = fullName || contact.emailAddress || t('mailTemplate.employeeSelection.unknownContact')
    const position = contact.position || ''

    let subjectDisplay
    if (hasNoSubject) {
      subjectDisplay = showGeneratedMessage ? t('mailTemplate.campaignReview.noSubjectGenerated') : '—'
    } else {
      subjectDisplay = contact.emailSubject
    }

    let contentPreview
    if (hasNoContent) {
      contentPreview = showGeneratedMessage ? t('mailTemplate.campaignReview.noContentGenerated') : '—'
    } else {
      const textContent = contact.emailContent.replace(/<[^>]*>/g, '')
      const decodedContent = textContent.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, '\'').replace(/&nbsp;/g, ' ')
      contentPreview = decodedContent.length > 80 ? decodedContent.substring(0, 80) + '...' : decodedContent
    }

    return (
      <tr
        key={contact.id}
        style={{
          backgroundColor: shouldHighlightEdited
            ? 'rgba(144, 202, 249, 0.3)'
            : shouldHighlightEmpty
              ? 'rgba(255, 243, 224, 0.5)'
              : 'rgba(245, 245, 245, 0.5)',
          borderLeft: shouldHighlightEdited
            ? '3px solid #2196f3'
            : shouldHighlightEmpty
              ? '3px solid #ff9800'
              : '3px solid transparent'
        }}
        className="group"
      >
        <td style={{ paddingLeft: '40px' }}></td>
        <td colSpan={2} style={{ paddingLeft: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{ fontStyle: isUnknownContact ? 'italic' : 'normal' }}
            >
              {contactName}
            </Typography>
            {position && (
              <Typography variant="caption" color="text.secondary">
                {position}
              </Typography>
            )}
          </Box>
        </td>
        <td colSpan={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('mailTemplate.wizard.firmList.emailSubject')}:
            </Typography>
            <Typography
              variant="body2"
              color={hasNoSubject && showGeneratedMessage ? 'warning.dark' : 'text.primary'}
              sx={{
                fontStyle: hasNoSubject ? 'italic' : 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '300px'
              }}
            >
              <Tooltip title={subjectDisplay} arrow placement="top" disableHoverListener={subjectDisplay.length <= 50}>
                <span>{subjectDisplay}</span>
              </Tooltip>
            </Typography>
          </Box>
        </td>
        <td>
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('mailTemplate.campaignReview.emailContent')}:
            </Typography>
            <Typography
              variant="body2"
              color={hasNoContent && showGeneratedMessage ? 'warning.dark' : 'text.primary'}
              sx={{
                fontStyle: hasNoContent ? 'italic' : 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '250px'
              }}
            >
              <Tooltip
                title={contentPreview}
                arrow
                placement="top"
                disableHoverListener={contentPreview.length <= 80}
              >
                <span>{contentPreview}</span>
              </Tooltip>
            </Typography>
          </Box>
        </td>
        <td style={{ textAlign: 'right', paddingRight: '16px' }}>
          {!disableInteraction && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation()
                if (onEdit) {
                  onEdit({ contact, firm })
                }
              }}
              sx={{
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                'tr:hover &': {
                  opacity: 1
                },
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                },
                borderRadius: 1,
                padding: '4px',
                minWidth: '24px',
                width: '24px',
                height: '24px'
              }}
            >
              <Edit sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', height: '100%' }}>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="overflow-x-auto" style={{ flex: 1 }}>
          <table className={tableStyles.table}>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    style={{
                      flexGrow: header.column.columnDef.meta?.flexGrow,
                      ...(header.column.columnDef.meta?.sticky === 'right'
                        ? {
                            position: 'sticky',
                            right: 0,
                            backgroundColor: 'white',
                            zIndex: 2,
                            borderLeft: '1px solid rgba(224, 224, 224, 0.5)'
                          }
                        : {})
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        )}
                  </th>
                ))}
              </tr>
            ))}
            </thead>
            <tbody>
            {table.getFilteredRowModel().rows.length === 0
              ? (
                <tr>
                  <td colSpan={table.getAllColumns().length} className="text-center py-8">
                    {fetching
                      ? (
                        <CircularProgress/>
                        )
                      : (
                        <Typography variant="body2" color="text.secondary">
                          {t('mailTemplate.campaignReview.noFirms')}
                        </Typography>
                        )}
                  </td>
                </tr>
                )
              : (
                  table.getRowModel().rows.slice(0, table.getState().pagination.pageSize).map(row => {
                    const selectedContacts = getSelectedContacts(row.original)
                    const isExpanded = expandedRows[row.original.identifier]

                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className="hover:bg-gray-50 group"
                          style={{
                            cursor: selectedContacts.length > 0 ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (selectedContacts.length > 0) {
                              toggleRowExpanded(row.original.identifier)
                            }
                          }}
                        >
                          {row.getVisibleCells().map(cell => (
                            <td
                              key={cell.id}
                              style={{
                                flexGrow: cell.column.columnDef.meta?.flexGrow,
                                ...(cell.column.columnDef.meta?.sticky === 'right'
                                  ? {
                                      position: 'sticky',
                                      right: 0,
                                      backgroundColor: 'white',
                                      zIndex: 1,
                                      borderLeft: '1px solid rgba(224, 224, 224, 0.5)'
                                    }
                                  : {})
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                        {isExpanded && selectedContacts.map(contact =>
                          renderContactSubRow(contact, row.original)
                        )}
                      </React.Fragment>
                    )
                  })
                )}
            </tbody>
          </table>
        </div>

        <div style={{ flexShrink: 0, marginTop: 'auto' }}>
          <TablePagination
            component={() => (
              <TablePaginationComponent
                table={table}
                infoText1={t('mailTemplate.campaignReview.tableInfo_1')}
                infoText2={t('mailTemplate.campaignReview.tableInfo_2')}
              />
            )}
            count={table.getFilteredRowModel().rows.length}
            rowsPerPage={table.getState().pagination.pageSize}
            page={table.getState().pagination.pageIndex}
            onPageChange={(_, page) => {
              table.setPageIndex(page)
            }}
          />
        </div>
      </div>

      {(campaignStatus === 'READY_TO_MAIL' || campaignStatus === 'MAILING') && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(224, 224, 224, 1)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '16px', height: '10px', backgroundColor: 'rgba(144, 202, 249, 0.3)', borderLeft: '3px solid #2196f3', borderRadius: '4px' }}></div>
            <Typography variant="caption" color="text.secondary">
              {t('mailTemplate.campaignReview.editedContent')}
            </Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '16px', height: '10px', backgroundColor: 'rgba(255, 243, 224, 0.5)', borderLeft: '3px solid #ff9800', borderRadius: '4px' }}></div>
            <Typography variant="caption" color="text.secondary">
              {t('mailTemplate.campaignReview.emptyContent')}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignReviewTable
